'use client'

import { emitToast } from '@lib/emitToast'
import type { BusProps } from '@lib/emitToast'

const DB_NAME = 'crm_notifications'
const DB_VERSION = 1
const STORE_NAME = 'notifications'
const CHANNEL_NAME = 'crm_notifications_channel'
const MAX_NOTIFICATIONS = 100
const EXPIRY_DAYS = 7
const CLEANUP_INTERVAL_MS = 60_000

export type NotificationType = 'error' | 'warning' | 'info' | 'success'

export interface NotificationAction {
  label: string
  actionType: string
  icon?: string
  payload?: Record<string, string>
}

export interface Notification {
  id: string
  type: NotificationType
  title: string
  message?: string
  timestamp: number
  persistent: boolean
  actions?: NotificationAction[]
  readAt?: number
}

export type NotificationPayload = Omit<Notification, 'timestamp' | 'readAt'> & { timestamp?: number }

const notificationBus = new EventTarget()

let db: IDBDatabase | null = null
let channel: BroadcastChannel | null = null
let lastCleanup = 0

function wrapIDBRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

function initDB(): Promise<IDBDatabase> {
  if (db) return Promise.resolve(db)

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => reject(request.error)
    request.onsuccess = () => {
      db = request.result
      resolve(db)
    }

    request.onupgradeneeded = (event) => {
      const database = (event.target as IDBOpenDBRequest).result
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: 'id' })
      }
    }
  })
}

function initChannel(): BroadcastChannel | null {
  if (channel) return channel

  try {
    channel = new BroadcastChannel(CHANNEL_NAME)
    channel.onmessage = (event) => {
      const { type, notification, id } = event.data

      if (type === 'emit') {
        notificationBus.dispatchEvent(new CustomEvent('notification', { detail: notification }))
      } else if (type === 'dismiss') {
        notificationBus.dispatchEvent(new CustomEvent('dismiss', { detail: id }))
      } else if (type === 'clear') {
        notificationBus.dispatchEvent(new CustomEvent('clear'))
      } else if (type === 'mark-read') {
        notificationBus.dispatchEvent(new CustomEvent('mark-read', { detail: event.data }))
      } else if (type === 'mark-unread') {
        notificationBus.dispatchEvent(new CustomEvent('mark-unread', { detail: id }))
      } else if (type === 'mark-all-read') {
        notificationBus.dispatchEvent(new CustomEvent('mark-all-read', { detail: event.data.readAt }))
      }
    }
  } catch {
    // BroadcastChannel may not be available in some contexts (e.g. incognito in older browsers)
    return null
  }

  return channel
}

async function cleanupOldNotifications(database: IDBDatabase): Promise<void> {
  const now = Date.now()
  if (now - lastCleanup < CLEANUP_INTERVAL_MS) return
  lastCleanup = now

  const cutoff = now - EXPIRY_DAYS * 24 * 60 * 60 * 1000

  // Read in one transaction (await may auto-commit it)
  const readStore = database.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME)
  const notifications: Notification[] = await wrapIDBRequest(readStore.getAll())

  const idsToDelete: string[] = []

  const expired = notifications.filter((n) => n.timestamp < cutoff)
  for (const n of expired) idsToDelete.push(n.id)

  const remaining = notifications.length - expired.length
  if (remaining > MAX_NOTIFICATIONS) {
    const sorted = notifications.filter((n) => n.timestamp >= cutoff).sort((a, b) => a.timestamp - b.timestamp)
    for (const n of sorted.slice(0, remaining - MAX_NOTIFICATIONS)) {
      idsToDelete.push(n.id)
    }
  }

  // Delete in a fresh transaction so it doesn't depend on the read transaction's lifetime
  if (idsToDelete.length > 0) {
    const writeStore = database.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME)
    for (const id of idsToDelete) writeStore.delete(id)
  }
}

export async function emitNotification(payload: NotificationPayload): Promise<void> {
  const database = await initDB()
  const bc = initChannel()

  await cleanupOldNotifications(database)

  const notification: Notification = {
    ...payload,
    timestamp: payload.timestamp ?? Date.now(),
    persistent: payload.persistent ?? true,
  }

  const store = database.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME)
  await wrapIDBRequest(store.put(notification))

  notificationBus.dispatchEvent(new CustomEvent('notification', { detail: notification }))
  bc?.postMessage({ type: 'emit', notification })
}

export async function dismissNotification(id: string): Promise<void> {
  const database = await initDB()
  const bc = initChannel()

  const store = database.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME)
  await wrapIDBRequest(store.delete(id))

  notificationBus.dispatchEvent(new CustomEvent('dismiss', { detail: id }))
  bc?.postMessage({ type: 'dismiss', id })
}

export async function clearAllNotifications(): Promise<void> {
  const database = await initDB()
  const bc = initChannel()

  const store = database.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME)
  await wrapIDBRequest(store.clear())

  notificationBus.dispatchEvent(new CustomEvent('clear'))
  bc?.postMessage({ type: 'clear' })
}

export async function getNotifications(): Promise<Notification[]> {
  const database = await initDB()

  const store = database.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME)
  const notifications: Notification[] = await wrapIDBRequest(store.getAll())

  return notifications.sort((a, b) => b.timestamp - a.timestamp)
}

/**
 * Emit to both the bell (persistent) and toast (ephemeral) in one call.
 * Use for events that should appear in both systems (e.g. WebSocket notifications).
 */
export async function emitWithToast(payload: NotificationPayload, toastOverrides?: Partial<BusProps>): Promise<void> {
  await emitNotification(payload)
  emitToast({
    message: [payload.title, payload.message].filter(Boolean).join(': '),
    type: payload.type,
    duration: payload.type === 'error' ? 5000 : 3000,
    ...toastOverrides,
  })
}

export async function markNotificationRead(id: string): Promise<void> {
  const database = await initDB()
  const bc = initChannel()

  const readStore = database.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME)
  const notification: Notification | undefined = await wrapIDBRequest(readStore.get(id))
  if (!notification) return

  const readAt = Date.now()
  const writeStore = database.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME)
  await wrapIDBRequest(writeStore.put({ ...notification, readAt }))

  const detail = { id, readAt }
  notificationBus.dispatchEvent(new CustomEvent('mark-read', { detail }))
  bc?.postMessage({ type: 'mark-read', ...detail })
}

export async function markNotificationUnread(id: string): Promise<void> {
  const database = await initDB()
  const bc = initChannel()

  const readStore = database.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME)
  const notification: Notification | undefined = await wrapIDBRequest(readStore.get(id))
  if (!notification) return

  const { readAt: _, ...rest } = notification
  const writeStore = database.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME)
  await wrapIDBRequest(writeStore.put(rest))

  notificationBus.dispatchEvent(new CustomEvent('mark-unread', { detail: id }))
  bc?.postMessage({ type: 'mark-unread', id })
}

export async function markAllNotificationsRead(): Promise<void> {
  const database = await initDB()
  const bc = initChannel()

  const store = database.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME)
  const notifications: Notification[] = await wrapIDBRequest(store.getAll())

  const readAt = Date.now()
  const unread = notifications.filter((n) => !n.readAt)
  if (unread.length === 0) return

  const writeStore = database.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME)
  for (const n of unread) writeStore.put({ ...n, readAt })

  notificationBus.dispatchEvent(new CustomEvent('mark-all-read', { detail: readAt }))
  bc?.postMessage({ type: 'mark-all-read', readAt })
}

export interface NotificationSubscribers {
  onNotification: (n: Notification) => void
  onDismiss?: (id: string) => void
  onClear?: () => void
  onMarkRead?: (id: string, readAt: number) => void
  onMarkUnread?: (id: string) => void
  onMarkAllRead?: (readAt: number) => void
}

export function subscribeNotifications(
  onNotificationOrSubs: ((n: Notification) => void) | NotificationSubscribers,
  onDismiss?: (id: string) => void,
  onClear?: () => void,
): () => void {
  initChannel()

  const subs: NotificationSubscribers =
    typeof onNotificationOrSubs === 'function'
      ? { onNotification: onNotificationOrSubs, onDismiss, onClear }
      : onNotificationOrSubs

  const handleNotification = (e: Event) => subs.onNotification((e as CustomEvent<Notification>).detail)
  const handleDismiss = (e: Event) => subs.onDismiss?.((e as CustomEvent<string>).detail)
  const handleClear = () => subs.onClear?.()
  const handleMarkRead = (e: Event) => {
    const detail = (e as CustomEvent<{ id: string; readAt: number }>).detail
    subs.onMarkRead?.(detail.id, detail.readAt)
  }
  const handleMarkUnread = (e: Event) => subs.onMarkUnread?.((e as CustomEvent<string>).detail)
  const handleMarkAllRead = (e: Event) => subs.onMarkAllRead?.((e as CustomEvent<number>).detail)

  notificationBus.addEventListener('notification', handleNotification)
  notificationBus.addEventListener('dismiss', handleDismiss)
  notificationBus.addEventListener('clear', handleClear)
  notificationBus.addEventListener('mark-read', handleMarkRead)
  notificationBus.addEventListener('mark-unread', handleMarkUnread)
  notificationBus.addEventListener('mark-all-read', handleMarkAllRead)

  return () => {
    notificationBus.removeEventListener('notification', handleNotification)
    notificationBus.removeEventListener('dismiss', handleDismiss)
    notificationBus.removeEventListener('clear', handleClear)
    notificationBus.removeEventListener('mark-read', handleMarkRead)
    notificationBus.removeEventListener('mark-unread', handleMarkUnread)
    notificationBus.removeEventListener('mark-all-read', handleMarkAllRead)
  }
}
